import { useCallback, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import { DIMENSIONALITY_VALUES } from 'constants/businessTool';
import { FOR_BANK_SECTIONS, FOR_BANK_SUB_SECTIONS } from 'constants/businessToolSections/forBank';
import { debounce } from 'lodash';
import { useGetPpeAssetsCalculations } from 'services/calculations/balanceSheet';

import useCheckSubSectionAccess from 'hooks/useCheckSubSectionAccess';

import { PERIODS_OPERATION_TYPES, summarizeSubtractPeriodValues } from 'components/BusinessToolForm/utils/helpers';

import ToastService from 'helpers/ToastService';

import { IFormContentTypes, observedFields } from '../../utils/form';

export const usePpeAssetsCalculation = () => {
  const { id: businessPlanId = '' } = useParams();
  const { setValue } = useFormContext();

  const getPpeAssetsCalculation = useGetPpeAssetsCalculations();

  const canAccess = useCheckSubSectionAccess(
    FOR_BANK_SECTIONS.MARKETING_AND_SALES,
    FOR_BANK_SUB_SECTIONS.FOR_BANK_MARKETING_AND_SALES.SALES_PLAN,
  );

  const [ppeFiledName, intangibleFiledName] = observedFields.ppeAssets.setCalculatedValueFieldNames;

  const debouncedUpdateFunction = useMemo(
    () =>
      debounce(async (ppeAssetsData, unit) => {
        const params = {
          unit,
          businessPlanId,
          ppeAssetsData,
        };

        try {
          const { intangibleAssets, propertyPlantAndEquipment } = await getPpeAssetsCalculation.mutateAsync(params);

          setValue(ppeFiledName, propertyPlantAndEquipment);
          setValue(intangibleFiledName, intangibleAssets);
        } catch (error) {
          if (error instanceof Error) ToastService.showError(error.message);
        }
      }, 500),

    [],
  );

  const ppeAssetsCalculationHandler = useCallback(
    async (content: IFormContentTypes['ppeAssets']) => {
      if (!canAccess) return;

      const [
        propertyPlantAndEquipment,
        intangibleAssets,
        investingActivitiesTable,
        depreciation,
        amortization,
        fundingDirectionTable,
        operationUnit,
        gainsAndLossesAssets,
      ] = content;

      const [purchaseOfIntangibleAndOtherNonCurrentAssets, proceedsFromSale, governmentGrants] = [
        investingActivitiesTable[0],
        investingActivitiesTable[2],
        investingActivitiesTable[4],
      ];
      const [construction, equipmentPurchase] = [fundingDirectionTable[3], fundingDirectionTable[4]];
      const gainsAndLosses = summarizeSubtractPeriodValues(
        [gainsAndLossesAssets, governmentGrants],
        PERIODS_OPERATION_TYPES.ADD,
        true,
      );

      const ppeAssetsData = {
        propertyPlantAndEquipment: Number(propertyPlantAndEquipment),
        intangibleAssets: Number(intangibleAssets),
        purchaseOfIntangibleAndOtherNonCurrentAssets,
        depreciation,
        amortization,
        construction,
        equipmentPurchase,
        proceedsFromSale,
        gainsAndLosses,
      };
      const unit = operationUnit ?? DIMENSIONALITY_VALUES.SINGLE;

      debouncedUpdateFunction(ppeAssetsData, unit);
    },

    [debouncedUpdateFunction],
  );

  return ppeAssetsCalculationHandler;
};
